import { Colors, Spinner } from '@amelio/ui-library';

import { FeatureFlagsUnavailableState } from './FeatureFlagsUnavailableState';

import type { ReactNode } from 'react';

import { FeatureFlag } from '@/env';
import { useFeatureFlag } from '@/hooks/utils/useFeatureFlag';

interface FeatureGateProps {
    // Narrower than `FeatureFlagKey` (a bare `string`): a route or page declaring its guard can
    // only name a flag that actually exists in `FeatureFlag`, not an arbitrary typo'd string.
    flag: (typeof FeatureFlag)[keyof typeof FeatureFlag];
    fallback: ReactNode;
    children: ReactNode;
}

/**
 * Guards a subtree behind a backend feature flag, rendering `fallback` instead while it is off.
 * Keeps route declarations declarative: a page component decides what "disabled" looks like, a
 * route only says which flag it depends on (see `RootNavigator`).
 *
 * Three answers, never two. While the backend's configuration is still in flight, nothing is known
 * yet and the gate waits on a spinner: rendering `fallback` during that window flashed "feature not
 * enabled" on every single page load -- measured on `localhost`, with a warm API. Once the request
 * has failed, nothing will be known, and the gate says so rather than passing an outage off as a
 * decision about the organization. Only a configuration that answered reaches `fallback`.
 */
export const FeatureGate = ({ flag, fallback, children }: FeatureGateProps) => {
    const { isFeatureEnabled, areFeatureFlagsKnown, areFeatureFlagsUnavailable } = useFeatureFlag();

    if (areFeatureFlagsUnavailable) {
        return <FeatureFlagsUnavailableState />;
    }

    if (!areFeatureFlagsKnown) {
        return (
            <div className={'flex justify-center py-10'}>
                <Spinner
                    size={'md'}
                    color={Colors.PRIMARY}
                    dataTestId={'feature-gate-loading'}
                />
            </div>
        );
    }

    return <>{isFeatureEnabled(flag) ? children : fallback}</>;
};
